import React, { useEffect, useMemo, useRef, useState } from 'react';

interface DatePickerProps {
  label?: string;
  value?: string | [string | undefined, string | undefined]; // ISO string: 'YYYY-MM-DD' or 'YYYY-MM-DDTHH:mm' or [start, end]
  onChange: (val: string | [string | undefined, string | undefined]) => void;
  placeholder?: string;
  required?: boolean;
  withTime?: boolean; // when true, returns 'YYYY-MM-DDTHH:mm'
  min?: string;
  max?: string;
  error?: string;
  range?: boolean; // New prop to enable date range selection
  className?: string; // Add className prop
}

function formatYMD(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function clamp(date: Date, min?: string, max?: string): Date {
  const t = date.getTime();
  if (min) {
    const mt = new Date(min).getTime();
    if (!isNaN(mt) && t < mt) return new Date(mt);
  }
  if (max) {
    const xt = new Date(max).getTime();
    if (!isNaN(xt) && t > xt) return new Date(xt);
  }
  return date;
}

const DatePicker: React.FC<DatePickerProps> = ({ label, value, onChange, placeholder, required, withTime, min, max, error, range }) => {
  const [open, setOpen] = useState(false);
  const [viewDate, setViewDate] = useState<Date>(() => {
    const initialValue = Array.isArray(value) ? value[0] : value;
    const parsed = initialValue ? new Date(initialValue) : new Date();
    return clamp(isNaN(parsed.getTime()) ? new Date() : parsed, min, max);
  });
  const [timeHH, setTimeHH] = useState<string>(() => {
    if (!withTime) return '00';
    const initialValue = Array.isArray(value) ? value[0] : value;
    if (!initialValue) return '00';
    const dd = new Date(initialValue);
    return isNaN(dd.getTime()) ? '00' : String(dd.getHours()).padStart(2, '0');
  });
  const [timeMM, setTimeMM] = useState<string>(() => {
    if (!withTime) return '00';
    const initialValue = Array.isArray(value) ? value[0] : value;
    if (!initialValue) return '00';
    const dd = new Date(initialValue);
    return isNaN(dd.getTime()) ? '00' : String(dd.getMinutes()).padStart(2, '0');
  });

  const [selectedDates, setSelectedDates] = useState<[Date | undefined, Date | undefined]>(() => {
    if (range && Array.isArray(value)) {
      return [value[0] ? new Date(value[0]) : undefined, value[1] ? new Date(value[1]) : undefined];
    }
    return [undefined, undefined];
  });

  useEffect(() => {
    if (range && Array.isArray(value)) {
      setSelectedDates([value[0] ? new Date(value[0]) : undefined, value[1] ? new Date(value[1]) : undefined]);
    } else if (!range && typeof value === 'string') {
      setSelectedDates([value ? new Date(value) : undefined, undefined]);
    }
  }, [value, range]);

  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const days: { date: Date; currentMonth: boolean }[] = useMemo(() => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDay = firstDay.getDay(); // 0-6
    const start = new Date(year, month, 1 - startDay);
    const arr: { date: Date; currentMonth: boolean }[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      arr.push({ date: d, currentMonth: d.getMonth() === month });
    }
    return arr;
  }, [viewDate]);

  const selectedYMD = useMemo(() => {
    if (!range && typeof value === 'string' && value) {
      return formatYMD(new Date(value));
    }
    return '';
  }, [value, range]);

  const applySelection = (d: Date) => {
    if (range) {
      let [start, end] = selectedDates;
      if (!start || (start && end)) {
        // First click or resetting range
        start = d;
        end = undefined;
      } else if (start && !end) {
        // Second click
        if (d.getTime() === start.getTime()) {
          // Same date selected twice, make it a full day range
          start.setHours(0, 0, 0, 0);
          end = new Date(d);
          end.setHours(23, 59, 59, 999);
        } else if (d < start) {
          end = start;
          start = d;
        } else {
          end = d;
        }
      }
      setSelectedDates([start, end]);
      if (start && end) {
        onChange([start.toISOString(), end.toISOString()]);
        setOpen(false);
      }
    } else {
      const clamped = clamp(d, min, max);
      if (withTime) {
        const hh = Number(timeHH) || 0;
        const mm = Number(timeMM) || 0;
        clamped.setHours(hh, mm, 0, 0);
        const iso = new Date(clamped);
        const ymd = formatYMD(iso);
        const full = `${ymd}T${String(iso.getHours()).padStart(2, '0')}:${String(iso.getMinutes()).padStart(2, '0')}`;
        onChange(full);
      } else {
        onChange(formatYMD(clamped));
      }
      setOpen(false);
    }
  };

  const displayText = useMemo(() => {
    if (range) {
      const [start, end] = selectedDates;
      if (start && end) {
        const startYMD = formatYMD(start).split('-').reverse().join('-');
        const endYMD = formatYMD(end).split('-').reverse().join('-');
        return `${startYMD} - ${endYMD}`;
      }
      return '';
    } else {
      if (!value) return '';
      const d = new Date(value as string);
      if (isNaN(d.getTime())) return '';
      const ymd = formatYMD(d).split('-').reverse().join('-'); // DD-MM-YYYY for display
      if (!withTime) return ymd;
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      return `${ymd} ${hh}:${mm}`;
    }
  }, [value, withTime, range, selectedDates]);

  const headerLabel = `${viewDate.toLocaleString(undefined, { month: 'long' })} ${viewDate.getFullYear()}`;
  const monthOptions = useMemo(() => Array.from({ length: 12 }, (_, i) => ({ value: i, label: new Date(2000, i, 1).toLocaleString(undefined, { month: 'long' }) })), []);
  const yearOptions = useMemo(() => {
    const current = new Date().getFullYear();
    const start = current - 60;
    const end = current + 20;
    const list: number[] = [];
    for (let y = start; y <= end; y++) list.push(y);
    return list;
  }, []);

  const isDateSelected = (date: Date) => {
    if (range) {
      const [start, end] = selectedDates;
      if (!start) return false;
      if (!end) return formatYMD(start) === formatYMD(date);
      return date >= start && date <= end;
    } else {
      return selectedYMD === formatYMD(date);
    }
  };

  const isDateInRange = (date: Date) => {
    if (range) {
      const [start, end] = selectedDates;
      if (start && end) {
        return date > start && date < end;
      }
    }
    return false;
  };

  return (
    <div className="w-full relative" ref={ref}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}{required ? ' *' : ''}</label>
      )}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-primary-teaGreen/20 ${
          error ? 'border-red-300 focus:border-red-500' : 'border-gray-200 focus:border-primary-teaGreen'
        } text-left flex items-center justify-between`}
      >
        <span className={`truncate ${displayText ? 'text-gray-900' : 'text-gray-400'}`}>
          {displayText || placeholder || (withTime ? 'Select date & time' : range ? 'Select date range' : 'Select date')}
        </span>
        <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </button>
      {error && (
        <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {error}
        </p>
      )}
      {open && (
        <div className="absolute left-0 top-full z-50 mt-2 w-full max-w-md bg-white rounded-2xl shadow-2xl border border-gray-100 p-4">
          <div className="flex items-center justify-between mb-3">
            <button type="button" className="p-2 rounded-lg hover:bg-gray-100" onClick={() => setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg>
            </button>
            <div className="flex items-center gap-2">
              <select
                value={viewDate.getMonth()}
                onChange={(e) => setViewDate(prev => new Date(prev.getFullYear(), Number(e.target.value), 1))}
                className="px-2 py-1 text-sm border border-gray-200 rounded-lg bg-white"
              >
                {monthOptions.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
              <select
                value={viewDate.getFullYear()}
                onChange={(e) => setViewDate(prev => new Date(Number(e.target.value), prev.getMonth(), 1))}
                className="px-2 py-1 text-sm border border-gray-200 rounded-lg bg-white"
              >
                {yearOptions.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <button type="button" className="p-2 rounded-lg hover:bg-gray-100" onClick={() => setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
            </button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-xs text-gray-500 mb-1">
            {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => (<div key={d} className="py-1">{d}</div>))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {days.map(({ date, currentMonth }, idx) => {
              const ymd = formatYMD(date);
              const disabled = (min && ymd < formatYMD(new Date(min))) || (max && ymd > formatYMD(new Date(max)));
              const isSelected = isDateSelected(date);
              const inRange = isDateInRange(date);
              return (
                <button
                  key={idx}
                  type="button"
                  disabled={!!disabled}
                  onClick={() => applySelection(date)}
                  className={`py-2 text-sm rounded-lg transition-colors ${
                    isSelected ? 'bg-primary-teaGreen text-primary-black' : inRange ? 'bg-primary-teaGreen/20 text-primary-black' : currentMonth ? 'text-gray-800 hover:bg-gray-100' : 'text-gray-400 hover:bg-gray-100'
                  } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>
          {withTime && !range && (
            <div className="mt-3 flex items-center gap-3">
              <input
                type="number"
                min={0}
                max={23}
                value={timeHH}
                onChange={(e) => setTimeHH(String(Math.max(0, Math.min(23, Number(e.target.value) || 0))).padStart(2, '0'))}
                className="w-16 px-3 py-2 border border-gray-300 rounded-lg"
              />
              <span className="text-gray-600">:</span>
              <input
                type="number"
                min={0}
                max={59}
                value={timeMM}
                onChange={(e) => setTimeMM(String(Math.max(0, Math.min(59, Number(e.target.value) || 0))).padStart(2, '0'))}
                className="w-16 px-3 py-2 border border-gray-300 rounded-lg"
              />
              <button type="button" className="ml-auto px-3 py-2 rounded-lg bg-primary-lightGreen text-white" onClick={() => applySelection(viewDate)}>
                Apply
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DatePicker;
